import store, { state, load, field, list, text, assetOr, markDiscovered } from './store'
import { mediaUrl } from './config'
import { fieldsModel } from './schema'

/**
 * 文案接管需要在 DOM 更新后把「原文案」替换成后台配置的新文案。
 * 原文案以 Text 节点首次渲染的值为准存在节点自身属性上，
 * 这样即使用户改了文案，再次渲染时仍能用原始 key 去匹配后台词典。
 */
const ORIG = '__cmsOriginal__'
const STYLE_ID = 'kanban-cms-vars'

// 后台自身、登录页不参与接管，避免把后台界面上的字也替换掉
const SKIP_ROUTE_NAME = ['login', 'cms', 'Error']

const ZH = /[\u4e00-\u9fa5]/

function scopeOf(vm) {
  const r = vm.$route
  if (!r) return ''
  const name = r.name
  if (!name) return ''
  if (SKIP_ROUTE_NAME.indexOf(name) > -1) return ''
  // 后台/登录路径一律跳过
  if (/^\/(cms|admin|h5)/.test(r.path || '')) return ''
  return name
}

function applyTextNodes(root, scope) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  const nodes = []
  while (walker.nextNode()) {
    const n = walker.currentNode
    const raw = n.nodeValue
    if (!raw || !raw.trim() || !ZH.test(raw)) continue
    const p = n.parentElement
    if (!p) continue
    if (p.closest && p.closest('[data-cms-ignore]')) continue
    if (p.tagName === 'SCRIPT' || p.tagName === 'STYLE' || p.tagName === 'TEXTAREA') continue
    nodes.push(n)
  }
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]
    if (n[ORIG] === undefined) n[ORIG] = n.nodeValue
    const key = n[ORIG].trim()
    markDiscovered(scope, key)
    const next = text(scope, key)
    if (next === key) {
      if (n.nodeValue !== n[ORIG]) n.nodeValue = n[ORIG]
      continue
    }
    // 用之前的整串做一次 replace，保留节点原有的首尾空白
    const replaced = n[ORIG].replace(key, next)
    if (n.nodeValue !== replaced) n.nodeValue = replaced
  }
}

/**
 * 图层接管：把后台配置的资源地址写进 :root 的 CSS 变量。
 * 样式里写成 url(var(--cms-img-xxx, url(默认图))) 的元素就能被后台替换。
 */
function applyAssetVars() {
  if (typeof document === 'undefined') return
  if (!state.version) return
  const out = []
  for (const scope of Object.keys(state.bundle || {})) {
    const assets = state.bundle[scope] && state.bundle[scope].assets
    if (!assets) continue
    for (const name of Object.keys(assets)) {
      const v = assets[name]
      if (!v) continue
      // 统一走 mediaUrl：后台存的 /media/... 会被补上部署前缀（dev 为 /cms-media，prod 为 /kanban/cms-media）
      out.push(`  ${name}: url("${mediaUrl(v)}");`)
    }
  }
  let style = document.getElementById(STYLE_ID)
  if (!out.length) {
    if (style) style.textContent = ''
    return
  }
  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ID
    document.head.appendChild(style)
  }
  const css = ':root {\n' + out.join('\n') + '\n}'
  if (style.textContent !== css) style.textContent = css
}

let lastVersion = -1
let assetCacheKey = ''

function assetKeyOf() {
  let s = ''
  for (const scope of Object.keys(state.bundle || {})) {
    const assets = state.bundle[scope] && state.bundle[scope].assets
    if (!assets) continue
    for (const name of Object.keys(assets)) s += name + '=' + (assets[name] || '') + ';'
  }
  return s
}

function hasTextOverride(scope) {
  const b = state.bundle[scope]
  return !!(b && b.texts && Object.keys(b.texts).length)
}

/**
 * 性能保护：页面没配任何文案/资源时只在首次渲染扫一遍（用于登记文案目录），
 * 之后直接跳过，避免 1s 级刷新的大屏每轮都做一次全量 DOM 遍历。
 */
function tick(vm) {
  if (!state.ready) return
  const scope = scopeOf(vm)
  const el = vm.$el
  if (!el || el.nodeType !== 1) return

  const key = assetKeyOf()
  if (key !== assetCacheKey) {
    assetCacheKey = key
    applyAssetVars()
  }

  if (!scope) return
  // 已迁移到 FIELDS 字段模型的页面：文案由 $C 直接读取远端 fields，
  // 不再走自动扫描接管，否则会把 $C 渲染出的默认中文当「原文案」登记并清空。
  if (fieldsModel(scope)) return
  const visited = el.__cmsVisited === state.version
  if (visited && !hasTextOverride(scope)) return
  el.__cmsVisited = state.version
  applyTextNodes(el, scope)
}

const CmsPlugin = {
  install(Vue) {
    Vue.prototype.$cms = store
    /** 取结构化字段：{{ $C(' corporate.name') }} 形式暂不使用；用 $C(scope, path, fallback) */
    Vue.prototype.$C = function (scope, path, fallback) {
      return field(scope, path, fallback)
    }
    Vue.prototype.$CList = function (scope, path, fallback) {
      return list(scope, path, fallback)
    }
    /** 素材替换：<video :src="$cmsMedia('--cms-video-ai', '/assets/ai/ai.webm')"> */
    Vue.prototype.$cmsMedia = function (name, fallback) {
      return assetOr(name, fallback)
    }

    Vue.mixin({
      mounted() {
        this.$nextTick(() => tick(this))
      },
      updated() {
        if (state.version !== lastVersion) {
          lastVersion = state.version
        }
        tick(this)
      },
    })

    // 启动即拉取；失败时静默使用本地默认内容
    load().then(() => {
      lastVersion = state.version
      applyAssetVars()
    })
  },
}

export { store, state, load, field, list, text }
export default CmsPlugin
